// Packages
import {
  Component,
  Element,
  Event as StencilEvent,
  EventEmitter,
  h,
  Host,
  Method,
  Prop,
  State,
  Watch,
} from "@stencil/core";

/**
 * Presentation surfaces supported by `fx-datetime`.
 */
export type TDatetimePresentation =
  | "time"
  | "date-time"
  | "month-year"
  | "month"
  | "year"
  | "date";

/**
 * Drill-down surfaces shown inside the calendar header navigation: the day
 * grid, the month chooser, or the year chooser.
 */
export type TDatetimeCalendarView = "days" | "months" | "years";

// Number of years shown per page in the calendar's year drill-down grid.
const YEAR_GRID_SIZE = 12;

/**
 * Highlight style applied to an individual day cell.
 */
export interface IDatetimeHighlightStyle {
  textColor?: string;
  backgroundColor?: string;
  border?: string;
}

/**
 * A highlighted date entry (used when `highlightedDates` is an array).
 */
export interface IDatetimeHighlight extends IDatetimeHighlightStyle {
  date: string;
}

/**
 * Callback form of `highlightedDates`.
 */
export type TDatetimeHighlightCallback = (
  iso: string,
) => IDatetimeHighlightStyle | undefined;

/**
 * Detail payload for the `fxChange` event.
 */
export interface IDatetimeChangeEventDetail {
  value: string | string[] | null;
}

interface IDatetimeParts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number; // 0-59
}

function pad(value: number, length: number = 2): string {
  return String(value).padStart(length, "0");
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function partsToIsoDate(year: number, month: number, day: number): string {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}

function partsToIso(parts: IDatetimeParts, presentation: TDatetimePresentation): string {
  switch (presentation) {
    case "time":
      return `${pad(parts.hour)}:${pad(parts.minute)}`;
    case "year":
      return pad(parts.year, 4);
    case "month":
    case "month-year":
      return `${pad(parts.year, 4)}-${pad(parts.month)}`;
    case "date":
      return partsToIsoDate(parts.year, parts.month, parts.day);
    case "date-time":
    default:
      return `${partsToIsoDate(parts.year, parts.month, parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}:00`;
  }
}

function parseIso(iso: string | undefined | null): Partial<IDatetimeParts> | null {
  if (typeof iso !== "string" || !iso.length) {
    return null;
  }
  const timeOnly = /^(\d{2}):(\d{2})/.exec(iso);
  if (timeOnly && iso.indexOf("-") === -1) {
    return { hour: Number(timeOnly[1]), minute: Number(timeOnly[2]) };
  }
  const match = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?(?:[T ](\d{2}):(\d{2}))?/.exec(iso);
  if (!match) {
    return null;
  }
  const parts: Partial<IDatetimeParts> = { year: Number(match[1]) };
  if (match[2] !== undefined) parts.month = Number(match[2]);
  if (match[3] !== undefined) parts.day = Number(match[3]);
  if (match[4] !== undefined) {
    parts.hour = Number(match[4]);
    parts.minute = Number(match[5]);
  }
  return parts;
}

function compareDate(a: IDatetimeParts, b: Partial<IDatetimeParts>): number {
  if (b.year !== undefined && a.year !== b.year) return a.year - b.year;
  if (b.month !== undefined && a.month !== b.month) return a.month - b.month;
  if (b.day !== undefined && a.day !== b.day) return a.day - b.day;
  return 0;
}

function parseNumberArray(raw: number[] | string | undefined): number[] | undefined {
  if (raw === undefined || raw === null) {
    return undefined;
  }
  if (Array.isArray(raw)) {
    return raw.filter((entry) => typeof entry === "number" && !Number.isNaN(entry));
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .map((entry: unknown) => Number(entry))
          .filter((entry: number) => !Number.isNaN(entry));
      }
      const single = Number(parsed);
      return Number.isNaN(single) ? undefined : [single];
    } catch {
      return raw
        .split(",")
        .map((entry) => Number(entry.trim()))
        .filter((entry) => !Number.isNaN(entry));
    }
  }
  return undefined;
}

/**
 * `fx-datetime` is a headless, accessible date and time picker. It supports
 * date grids, month/year lists, and time columns through the `presentation`
 * prop, single and multiple selection, min/max limits, allowed-value
 * restriction, per-day enabling, and per-day highlighting. It ships no
 * internal styles; every styleable surface is exposed via `part` attributes
 * and its state through `data-*` and ARIA attributes.
 *
 * @part base - The wrapper around the whole picker.
 * @part calendar - The wrapper around the calendar (date) surface.
 * @part header - The month/year navigation header of the date grid.
 * @part prev-button - The previous-month navigation button.
 * @part next-button - The next-month navigation button.
 * @part month-year-label - The header's center label button. Clicking it
 * drills down: day grid -> month chooser -> year chooser -> day grid.
 * @part calendar-months - The month chooser wrapper (header drill-down).
 * @part calendar-month - A single month button in the month chooser.
 * @part calendar-month-current - The month matching the current view.
 * @part calendar-years - The year chooser wrapper (header drill-down).
 * @part calendar-year - A single year button in the year chooser.
 * @part calendar-year-current - The year matching the current view.
 * @part weekdays - The row of weekday names.
 * @part weekday - A single weekday name.
 * @part grid - The day grid.
 * @part row - A week row inside the day grid.
 * @part day - A single day cell button.
 * @part day-adjacent - A day cell from the previous/next month.
 * @part months - The month list wrapper.
 * @part month - A single month button.
 * @part years - The year list wrapper.
 * @part year - A single year button.
 * @part select-all-years - The select-all control in the multiple year view.
 * @part time - The time picker wrapper.
 * @part hours - The hour column wrapper.
 * @part hour - A single hour button.
 * @part minutes - The minute column wrapper.
 * @part minute - A single minute button.
 */
// `styleUrl` is intentionally omitted (no internal styles per AGENTS.md).
@Component({
  tag: "fx-datetime",
  shadow: true,
})
export class FxDatetime {
  // --------------------------------------------
  // Element / internal references
  // --------------------------------------------

  @Element() el!: HTMLElement;

  private initialValue: string | string[] | null = null;

  private hasFocus: boolean = false;

  // Guards the `value` watcher when we update the prop internally.
  private isInternalValueUpdate: boolean = false;

  // --------------------------------------------
  // State
  // --------------------------------------------

  /**
   * The year currently shown by the calendar/month/year surfaces.
   */
  @State()
  private viewYear: number = new Date().getFullYear();

  /**
   * The month (1-12) currently shown by the calendar surface.
   */
  @State()
  private viewMonth: number = new Date().getMonth() + 1;

  /**
   * The working (not yet confirmed) selection.
   */
  @State()
  private workingValues: string[] = [];

  /**
   * Which drill-down surface the calendar header currently shows: the day
   * grid, the month picker, or the year picker.
   */
  @State()
  private calendarView: TDatetimeCalendarView = "days";

  /**
   * First year shown by the calendar's year drill-down grid.
   */
  @State()
  private yearGridStart: number = new Date().getFullYear();

  // --------------------------------------------
  // Props
  // --------------------------------------------

  /**
   * @prop - The selected value as an ISO 8601 string, or an array of ISO 8601
   * strings when `multiple` is `true`.
   */
  @Prop({ mutable: true })
  value?: string | string[] | null;

  /**
   * @prop - If `true`, the picker is disabled and cannot be interacted with.
   * Reflected via `data-disabled` and `aria-disabled`.
   */
  @Prop()
  disabled: boolean = false;

  /**
   * @prop - If `true`, the picker is read-only: the current value is shown
   * but cannot be changed. Reflected via `data-readonly` and `aria-readonly`.
   */
  @Prop()
  readonly: boolean = false;

  /**
   * @prop - Theme color name. Reflected via `data-color` for theme styling.
   */
  @Prop()
  color?: string;

  /**
   * @prop - The earliest selectable date/time as an ISO 8601 string.
   */
  @Prop()
  min?: string;

  /**
   * @prop - The latest selectable date/time as an ISO 8601 string.
   */
  @Prop()
  max?: string;

  /**
   * @prop - Allowed month numbers (1-12). Accepts a number array property or
   * a JSON string attribute (e.g. `"[1,2,3]"`).
   */
  @Prop()
  monthValues?: number[] | string;

  /**
   * @prop - Allowed day-of-month numbers (1-31). Accepts a number array
   * property or a JSON string attribute.
   */
  @Prop()
  dayValues?: number[] | string;

  /**
   * @prop - Allowed year numbers. Accepts a number array property or a JSON
   * string attribute.
   */
  @Prop()
  yearValues?: number[] | string;

  /**
   * @prop - Allowed hour numbers (0-23). Accepts a number array property or
   * a JSON string attribute.
   */
  @Prop()
  hourValues?: number[] | string;

  /**
   * @prop - Allowed minute numbers (0-59). Accepts a number array property or
   * a JSON string attribute.
   */
  @Prop()
  minuteValues?: number[] | string;

  /**
   * @prop - Callback that decides whether an individual calendar day is
   * enabled. Called once per rendered day (including adjacent-month days)
   * with the day's ISO date string (`YYYY-MM-DD`).
   */
  @Prop()
  isDateEnabled?: (iso: string) => boolean;

  /**
   * @prop - Explicit BCP 47 locale (e.g. `"en-US"`). When omitted and
   * `disableAutoLocale` is `false`, the browser locale is used.
   */
  @Prop()
  locale?: string;

  /**
   * @prop - If `true`, the browser locale is ignored and only the explicit
   * `locale` prop is used (falling back to `"en-US"`).
   */
  @Prop()
  disableAutoLocale: boolean = false;

  /**
   * @prop - Which picker surface(s) to render.
   */
  @Prop()
  presentation: TDatetimePresentation = "date-time";

  /**
   * @prop - First day of the week: 0 (Sunday) through 6 (Saturday).
   */
  @Prop()
  firstDayOfWeek: number = 0;

  /**
   * @prop - If `true`, days from the previous/next month are rendered in the
   * date grid.
   */
  @Prop()
  showAdjacentDays: boolean = false;

  /**
   * @prop - If `true`, multiple values can be selected and `value` is an
   * array of ISO strings.
   */
  @Prop()
  multiple: boolean = false;

  /**
   * @prop - Highlight configuration for calendar days. Either an array of
   * `{ date, textColor?, backgroundColor?, border? }` entries or a callback
   * receiving each day's ISO date string. Accepts a JSON string attribute
   * for the array form. Highlight colors are applied as inline styles on the
   * matching day cell (user-provided data, not component theming) and the
   * cell is marked with `data-highlighted`.
   */
  @Prop()
  highlightedDates?: IDatetimeHighlight[] | TDatetimeHighlightCallback | string;

  // --------------------------------------------
  // Watchers
  // --------------------------------------------

  @Watch("value")
  protected onValueChanged() {
    if (this.isInternalValueUpdate) {
      return;
    }
    this.syncFromValue();
  }

  @Watch("presentation")
  @Watch("min")
  @Watch("max")
  protected onViewConfigChanged() {
    this.syncFromValue();
  }

  // --------------------------------------------
  // Events
  // --------------------------------------------

  /**
   * @event - Emitted when the value changes.
   */
  @StencilEvent()
  fxChange!: EventEmitter<IDatetimeChangeEventDetail>;

  /**
   * @event - Emitted when focus enters the component.
   */
  @StencilEvent()
  fxFocus!: EventEmitter<void>;

  /**
   * @event - Emitted when focus leaves the component.
   */
  @StencilEvent()
  fxBlur!: EventEmitter<void>;

  /**
   * @event - Emitted when the current selection is cancelled.
   */
  @StencilEvent()
  fxCancel!: EventEmitter<void>;

  // --------------------------------------------
  // Methods
  // --------------------------------------------

  /**
   * Cancels the current (unconfirmed) selection, reverts the working
   * selection to the confirmed `value`, and emits `fxCancel`.
   */
  @Method()
  async cancel(): Promise<void> {
    this.workingValues = this.normalizeValue(this.value);
    this.fxCancel.emit();
  }

  /**
   * Confirms the current working selection, updates `value`, and emits
   * `fxChange`.
   */
  @Method()
  async confirm(): Promise<void> {
    this.applyWorkingValues(true);
  }

  /**
   * Resets the selection to the initial `value` the component loaded with.
   */
  @Method()
  async reset(): Promise<void> {
    this.setValueInternal(this.initialValue);
    this.workingValues = this.normalizeValue(this.initialValue);
    this.syncViewFromWorking();
  }

  // --------------------------------------------
  // Lifecycle
  // --------------------------------------------

  componentWillLoad() {
    this.initialValue = this.cloneValue(this.value ?? null);
    this.syncFromValue();
    this.ensureAccessibleName();
  }

  private ensureAccessibleName(): void {
    if (
      !this.el.hasAttribute("aria-label") &&
      !this.el.hasAttribute("aria-labelledby")
    ) {
      this.el.setAttribute("aria-label", "Date picker");
    }
  }

  // --------------------------------------------
  // Value helpers
  // --------------------------------------------

  private cloneValue(value: string | string[] | null): string | string[] | null {
    return Array.isArray(value) ? [...value] : value;
  }

  private normalizeValue(value: string | string[] | null | undefined): string[] {
    if (value === undefined || value === null || value === "") {
      return [];
    }
    if (Array.isArray(value)) {
      return value.filter((entry) => typeof entry === "string" && entry.length > 0);
    }
    if (typeof value === "string" && value.trim().startsWith("[")) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.filter(
            (entry: unknown): entry is string => typeof entry === "string" && entry.length > 0,
          );
        }
      } catch {
        return [value];
      }
    }
    return [value];
  }

  private syncFromValue() {
    this.workingValues = this.normalizeValue(this.value);
    this.syncViewFromWorking();
    this.calendarView = "days";
  }

  private syncViewFromWorking() {
    const first = parseIso(this.workingValues[0]) ?? parseIso(this.min) ?? {};
    const now = new Date();
    this.viewYear = first.year ?? now.getFullYear();
    this.viewMonth = first.month ?? now.getMonth() + 1;
  }

  private get effectiveLocale(): string {
    if (this.locale) {
      return this.locale;
    }
    if (!this.disableAutoLocale && typeof navigator !== "undefined" && navigator.language) {
      return navigator.language;
    }
    return "en-US";
  }

  private get isInteractive(): boolean {
    return !this.disabled && !this.readonly;
  }

  private get activeParts(): IDatetimeParts {
    const now = new Date();
    const parsed = parseIso(this.workingValues[0]) ?? {};
    const year = parsed.year ?? this.viewYear ?? now.getFullYear();
    const month = parsed.month ?? this.viewMonth ?? now.getMonth() + 1;
    const fallbackDay = parsed.day ?? now.getDate();
    return {
      year,
      month,
      day: Math.min(fallbackDay, daysInMonth(year, month)),
      hour: parsed.hour ?? 0,
      minute: parsed.minute ?? 0,
    };
  }

  private setValueInternal(value: string | string[] | null) {
    this.isInternalValueUpdate = true;
    this.value = value;
    this.isInternalValueUpdate = false;
  }

  private applyWorkingValues(forceEmit: boolean = false) {
    const next: string | string[] | null = this.multiple
      ? [...this.workingValues]
      : this.workingValues[0] ?? null;
    const previous = JSON.stringify(this.normalizeValue(this.value));
    const current = JSON.stringify(this.workingValues);
    this.setValueInternal(next);
    if (forceEmit || previous !== current) {
      this.fxChange.emit({ value: next });
    }
  }

  private updateSelection(iso: string) {
    if (!this.isInteractive) {
      return;
    }
    if (this.multiple) {
      const index = this.workingValues.indexOf(iso);
      this.workingValues =
        index === -1
          ? [...this.workingValues, iso]
          : this.workingValues.filter((entry) => entry !== iso);
    } else {
      this.workingValues = [iso];
    }
    this.applyWorkingValues();
  }

  private updateTimeSelection(hour: number | null, minute: number | null) {
    if (!this.isInteractive) {
      return;
    }
    const parts = this.activeParts;
    const next: IDatetimeParts = {
      ...parts,
      hour: hour ?? parts.hour,
      minute: minute ?? parts.minute,
    };
    this.workingValues = [partsToIso(next, this.presentation)];
    this.applyWorkingValues();
  }

  // --------------------------------------------
  // Restriction helpers
  // --------------------------------------------

  private isWithinMinMax(parts: IDatetimeParts): boolean {
    const minParts = parseIso(this.min);
    if (minParts && compareDate(parts, minParts) < 0) {
      return false;
    }
    const maxParts = parseIso(this.max);
    if (maxParts && compareDate(parts, maxParts) > 0) {
      return false;
    }
    return true;
  }

  private isAllowed(values: number[] | undefined, entry: number): boolean {
    return values === undefined || values.length === 0 || values.indexOf(entry) !== -1;
  }

  private isDaySelectable(
    parts: IDatetimeParts,
    iso: string,
    months: number[] | undefined,
    days: number[] | undefined,
    years: number[] | undefined,
  ): boolean {
    if (!this.isWithinMinMax(parts)) {
      return false;
    }
    if (
      !this.isAllowed(months, parts.month) ||
      !this.isAllowed(days, parts.day) ||
      !this.isAllowed(years, parts.year)
    ) {
      return false;
    }
    if (typeof this.isDateEnabled === "function") {
      try {
        return this.isDateEnabled(iso) !== false;
      } catch {
        return true;
      }
    }
    return true;
  }

  private getHighlightResolver(): TDatetimeHighlightCallback | null {
    const raw = this.highlightedDates;
    if (!raw) {
      return null;
    }
    if (typeof raw === "function") {
      return raw;
    }
    let entries: IDatetimeHighlight[] | null = null;
    if (Array.isArray(raw)) {
      entries = raw;
    } else if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        entries = Array.isArray(parsed) ? parsed : null;
      } catch {
        entries = null;
      }
    }
    if (!entries || !entries.length) {
      return null;
    }
    const map = new Map<string, IDatetimeHighlightStyle>();
    for (const entry of entries) {
      if (entry && typeof entry.date === "string") {
        map.set(entry.date, entry);
      }
    }
    return (iso: string) => map.get(iso);
  }

  // Applies user-provided highlight colors as important inline styles so they
  // win over theme `::part()` rules (which outrank plain inline styles on
  // shadow parts).
  private applyHighlightStyles(
    node: HTMLElement | undefined,
    highlight: IDatetimeHighlightStyle | undefined,
  ) {
    if (!node) {
      return;
    }
    node.style.removeProperty("color");
    node.style.removeProperty("background-color");
    node.style.removeProperty("border");
    if (!highlight) {
      return;
    }
    if (highlight.textColor) {
      node.style.setProperty("color", highlight.textColor, "important");
    }
    if (highlight.backgroundColor) {
      node.style.setProperty("background-color", highlight.backgroundColor, "important");
    }
    if (highlight.border) {
      node.style.setProperty("border", highlight.border, "important");
    }
  }

  // --------------------------------------------
  // Selection state helpers
  // --------------------------------------------

  private isDateSelected(iso: string): boolean {
    return this.workingValues.some((entry) => entry === iso || entry.startsWith(`${iso}T`));
  }

  private isMonthSelected(year: number, month: number): boolean {
    const prefix = `${pad(year, 4)}-${pad(month)}`;
    return this.workingValues.some(
      (entry) => entry === prefix || entry.startsWith(`${prefix}-`) || entry.startsWith(`${prefix}T`),
    );
  }

  private isYearSelected(year: number): boolean {
    const prefix = pad(year, 4);
    return this.workingValues.some((entry) => entry === prefix || entry.startsWith(`${prefix}-`));
  }

  // --------------------------------------------
  // Navigation
  // --------------------------------------------

  private navigateMonth(offset: number) {
    if (!this.isInteractive) {
      return;
    }
    let month = this.viewMonth + offset;
    let year = this.viewYear;
    if (month < 1) {
      month = 12;
      year -= 1;
    } else if (month > 12) {
      month = 1;
      year += 1;
    }
    this.viewMonth = month;
    this.viewYear = year;
  }

  private navigateViewYear(offset: number) {
    if (!this.isInteractive) {
      return;
    }
    this.viewYear += offset;
  }

  private navigateYearGrid(offset: number) {
    if (!this.isInteractive) {
      return;
    }
    this.yearGridStart += offset * YEAR_GRID_SIZE;
  }

  // Aligns the year grid to a decade-ish page that contains the given year.
  private yearGridStartFor(year: number): number {
    return Math.floor(year / 10) * 10;
  }

  private openCalendarView(view: TDatetimeCalendarView) {
    if (!this.isInteractive) {
      return;
    }
    if (view === "years") {
      this.yearGridStart = this.yearGridStartFor(this.viewYear);
    }
    this.calendarView = view;
  }

  // Picks a month from the drill-down (navigation only, no value change) and
  // returns to the day grid.
  private pickCalendarMonth(month: number) {
    if (!this.isInteractive) {
      return;
    }
    this.viewMonth = month;
    this.calendarView = "days";
  }

  // Picks a year from the drill-down (navigation only) and steps down to the
  // month chooser.
  private pickCalendarYear(year: number) {
    if (!this.isInteractive) {
      return;
    }
    this.viewYear = year;
    this.calendarView = "months";
  }

  // --------------------------------------------
  // Focus handling
  // --------------------------------------------

  private handleFocusIn = () => {
    if (!this.hasFocus) {
      this.hasFocus = true;
      this.fxFocus.emit();
    }
  };

  private handleFocusOut = (event: FocusEvent) => {
    const next = event.relatedTarget as Node | null;
    if (next && this.el.contains(next)) {
      return;
    }
    if (this.hasFocus) {
      this.hasFocus = false;
      this.fxBlur.emit();
    }
  };

  private handleGridKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement;
    if (!target || target.getAttribute("data-date") === null) {
      return;
    }
    const offsets: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    const offset = offsets[event.key];
    if (offset === undefined) {
      return;
    }
    event.preventDefault();
    const days = Array.from(
      this.el.shadowRoot!.querySelectorAll<HTMLButtonElement>("button[data-date]:not([disabled])"),
    );
    const index = days.indexOf(target as HTMLButtonElement);
    if (index === -1) {
      return;
    }
    const nextIndex = Math.min(Math.max(index + offset, 0), days.length - 1);
    days[nextIndex]?.focus();
  };

  // --------------------------------------------
  // Render helpers
  // --------------------------------------------

  private renderCalendar() {
    const locale = this.effectiveLocale;
    const months = parseNumberArray(this.monthValues);
    const days = parseNumberArray(this.dayValues);
    const years = parseNumberArray(this.yearValues);
    const highlightResolver = this.getHighlightResolver();
    const interactive = this.isInteractive;

    const weekdayFormatter = new Intl.DateTimeFormat(locale, { weekday: "short" });

    const firstDay = ((this.firstDayOfWeek % 7) + 7) % 7;
    const weekdays: string[] = [];
    for (let index = 0; index < 7; index++) {
      // 2023-01-01 was a Sunday; offset from it to get localized weekday names.
      const weekday = (firstDay + index) % 7;
      weekdays.push(weekdayFormatter.format(new Date(2023, 0, 1 + weekday)));
    }

    const totalDays = daysInMonth(this.viewYear, this.viewMonth);
    const firstWeekday = new Date(this.viewYear, this.viewMonth - 1, 1).getDay();
    const leading = (firstWeekday - firstDay + 7) % 7;

    interface IDayCell {
      parts: IDatetimeParts;
      adjacent: boolean;
    }

    const cells: (IDayCell | null)[] = [];
    const prevMonth = this.viewMonth === 1 ? 12 : this.viewMonth - 1;
    const prevYear = this.viewMonth === 1 ? this.viewYear - 1 : this.viewYear;
    const prevTotal = daysInMonth(prevYear, prevMonth);
    for (let index = 0; index < leading; index++) {
      cells.push(
        this.showAdjacentDays
          ? {
              parts: {
                year: prevYear,
                month: prevMonth,
                day: prevTotal - leading + 1 + index,
                hour: 0,
                minute: 0,
              },
              adjacent: true,
            }
          : null,
      );
    }
    for (let day = 1; day <= totalDays; day++) {
      cells.push({
        parts: { year: this.viewYear, month: this.viewMonth, day, hour: 0, minute: 0 },
        adjacent: false,
      });
    }
    const nextMonth = this.viewMonth === 12 ? 1 : this.viewMonth + 1;
    const nextYear = this.viewMonth === 12 ? this.viewYear + 1 : this.viewYear;
    let trailingDay = 1;
    while (cells.length % 7 !== 0) {
      cells.push(
        this.showAdjacentDays
          ? {
              parts: { year: nextYear, month: nextMonth, day: trailingDay++, hour: 0, minute: 0 },
              adjacent: true,
            }
          : null,
      );
    }

    const rows: (IDayCell | null)[][] = [];
    for (let index = 0; index < cells.length; index += 7) {
      rows.push(cells.slice(index, index + 7));
    }

    if (this.calendarView === "months") {
      return (
        <div
          part="calendar"
          data-view={`${pad(this.viewYear, 4)}-${pad(this.viewMonth)}`}
          data-calendar-view="months"
        >
          {this.renderCalendarHeader(locale)}
          {this.renderCalendarMonthGrid(locale)}
        </div>
      );
    }

    if (this.calendarView === "years") {
      return (
        <div
          part="calendar"
          data-view={`${pad(this.viewYear, 4)}-${pad(this.viewMonth)}`}
          data-calendar-view="years"
        >
          {this.renderCalendarHeader(locale)}
          {this.renderCalendarYearGrid()}
        </div>
      );
    }

    return (
      <div
        part="calendar"
        data-view={`${pad(this.viewYear, 4)}-${pad(this.viewMonth)}`}
        data-calendar-view="days"
      >
        {this.renderCalendarHeader(locale)}
        <div part="weekdays" role="row">
          {weekdays.map((weekday) => (
            <span part="weekday" role="columnheader">
              {weekday}
            </span>
          ))}
        </div>
        <div part="grid" role="grid" onKeyDown={this.handleGridKeyDown}>
          {rows.map((row) => (
            <div part="row" role="row">
              {row.map((cell) => {
                if (cell === null) {
                  return <span part="day-placeholder" role="gridcell" aria-hidden="true"></span>;
                }
                const { parts, adjacent } = cell;
                const iso = partsToIsoDate(parts.year, parts.month, parts.day);
                const selectable =
                  interactive && this.isDaySelectable(parts, iso, months, days, years);
                const selected = this.isDateSelected(iso);
                const highlight = highlightResolver ? highlightResolver(iso) : undefined;
                const selectedIso = this.presentation === "date" ? iso : partsToIso(
                  { ...parts, hour: this.activeParts.hour, minute: this.activeParts.minute },
                  this.presentation,
                );
                return (
                  <button
                    type="button"
                    role="gridcell"
                    part={`day${adjacent ? " day-adjacent" : ""}${selected ? " day-selected" : ""}`}
                    data-date={iso}
                    data-adjacent={adjacent ? "true" : null}
                    data-highlighted={highlight ? "true" : null}
                    aria-selected={selected ? "true" : "false"}
                    disabled={!selectable}
                    ref={(node) => this.applyHighlightStyles(node, highlight)}
                    onClick={() => this.updateSelection(selectedIso)}
                  >
                    {parts.day}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Header for the calendar surface. Its center label is a button that drills
  // down through days -> months -> years and back, and the prev/next buttons
  // step by month, year, or year page depending on the active view.
  private renderCalendarHeader(locale: string) {
    const interactive = this.isInteractive;
    const view = this.calendarView;

    let label: string;
    let labelAriaLabel: string;
    let nextView: TDatetimeCalendarView;
    let onPrev: () => void;
    let onNext: () => void;
    let prevAriaLabel: string;
    let nextAriaLabel: string;

    if (view === "months") {
      label = String(this.viewYear);
      labelAriaLabel = `${this.viewYear}, choose year`;
      nextView = "years";
      onPrev = () => this.navigateViewYear(-1);
      onNext = () => this.navigateViewYear(1);
      prevAriaLabel = "Previous year";
      nextAriaLabel = "Next year";
    } else if (view === "years") {
      const end = this.yearGridStart + YEAR_GRID_SIZE - 1;
      label = `${this.yearGridStart} - ${end}`;
      labelAriaLabel = `${label}, back to days`;
      nextView = "days";
      onPrev = () => this.navigateYearGrid(-1);
      onNext = () => this.navigateYearGrid(1);
      prevAriaLabel = "Previous years";
      nextAriaLabel = "Next years";
    } else {
      const monthYearFormatter = new Intl.DateTimeFormat(locale, {
        month: "long",
        year: "numeric",
      });
      label = monthYearFormatter.format(new Date(this.viewYear, this.viewMonth - 1, 1));
      labelAriaLabel = `${label}, choose month`;
      nextView = "months";
      onPrev = () => this.navigateMonth(-1);
      onNext = () => this.navigateMonth(1);
      prevAriaLabel = "Previous month";
      nextAriaLabel = "Next month";
    }

    return (
      <div part="header" data-calendar-view={view}>
        <button
          type="button"
          part="prev-button"
          aria-label={prevAriaLabel}
          disabled={!interactive}
          onClick={onPrev}
        >
          &#8249;
        </button>
        <button
          type="button"
          part="month-year-label"
          aria-live="polite"
          aria-label={labelAriaLabel}
          disabled={!interactive}
          onClick={() => this.openCalendarView(nextView)}
        >
          {label}
        </button>
        <button
          type="button"
          part="next-button"
          aria-label={nextAriaLabel}
          disabled={!interactive}
          onClick={onNext}
        >
          &#8250;
        </button>
      </div>
    );
  }

  // Month chooser shown when drilling down from the calendar header. Selecting
  // a month only navigates the day grid; it does not change the value.
  private renderCalendarMonthGrid(locale: string) {
    const interactive = this.isInteractive;
    const monthFormatter = new Intl.DateTimeFormat(locale, { month: "short" });

    return (
      <div part="calendar-months" role="listbox" aria-label="Choose month">
        {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => {
          const current = month === this.viewMonth;
          return (
            <button
              type="button"
              role="option"
              part={current ? "calendar-month calendar-month-current" : "calendar-month"}
              data-nav-month={String(month)}
              aria-selected={current ? "true" : "false"}
              disabled={!interactive}
              onClick={() => this.pickCalendarMonth(month)}
            >
              {monthFormatter.format(new Date(2023, month - 1, 1))}
            </button>
          );
        })}
      </div>
    );
  }

  // Year chooser shown when drilling down from the calendar header. Selecting
  // a year navigates to the month chooser; it does not change the value.
  private renderCalendarYearGrid() {
    const interactive = this.isInteractive;

    return (
      <div part="calendar-years" role="listbox" aria-label="Choose year">
        {Array.from({ length: YEAR_GRID_SIZE }, (_, index) => this.yearGridStart + index).map(
          (year) => {
            const current = year === this.viewYear;
            return (
              <button
                type="button"
                role="option"
                part={current ? "calendar-year calendar-year-current" : "calendar-year"}
                data-nav-year={String(year)}
                aria-selected={current ? "true" : "false"}
                disabled={!interactive}
                onClick={() => this.pickCalendarYear(year)}
              >
                {year}
              </button>
            );
          },
        )}
      </div>
    );
  }

  private renderMonths() {
    const locale = this.effectiveLocale;
    const months = parseNumberArray(this.monthValues);
    const interactive = this.isInteractive;
    const monthFormatter = new Intl.DateTimeFormat(locale, { month: "long" });
    const withYear = this.presentation !== "month";

    return (
      <div part="months" role="listbox" aria-label="Month">
        {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => {
          const allowed = this.isAllowed(months, month);
          const selected = withYear
            ? this.isMonthSelected(this.viewYear, month)
            : this.workingValues.some((entry) => parseIso(entry)?.month === month);
          const iso = `${pad(this.viewYear, 4)}-${pad(month)}`;
          return (
            <button
              type="button"
              role="option"
              part={selected ? "month month-selected" : "month"}
              data-month={String(month)}
              aria-selected={selected ? "true" : "false"}
              disabled={!interactive || !allowed}
              onClick={() => this.updateSelection(iso)}
            >
              {monthFormatter.format(new Date(2023, month - 1, 1))}
            </button>
          );
        })}
      </div>
    );
  }

  private getYearRange(): number[] {
    const years = parseNumberArray(this.yearValues);
    if (years && years.length) {
      return [...years].sort((a, b) => a - b);
    }
    const minParts = parseIso(this.min);
    const maxParts = parseIso(this.max);
    const current = new Date().getFullYear();
    const start = minParts?.year ?? current - 10;
    const end = maxParts?.year ?? current + 10;
    const range: number[] = [];
    for (let year = start; year <= end; year++) {
      range.push(year);
    }
    return range;
  }

  private toggleAllYears(years: number[]) {
    if (!this.isInteractive || !this.multiple) {
      return;
    }
    const isoYears = years.map((year) => pad(year, 4));
    const allSelected = isoYears.every((iso) => this.workingValues.indexOf(iso) !== -1);
    this.workingValues = allSelected ? [] : isoYears;
    this.applyWorkingValues();
  }

  private renderYears() {
    const interactive = this.isInteractive;
    const years = this.getYearRange();
    const minParts = parseIso(this.min);
    const maxParts = parseIso(this.max);
    const showSelectAll = this.multiple && this.presentation === "year";
    const allSelected =
      showSelectAll && years.every((year) => this.workingValues.indexOf(pad(year, 4)) !== -1);

    return (
      <div part="years" role="listbox" aria-label="Year" aria-multiselectable={this.multiple ? "true" : null}>
        {showSelectAll && (
          <button
            type="button"
            part="select-all-years"
            aria-pressed={allSelected ? "true" : "false"}
            disabled={!interactive}
            onClick={() => this.toggleAllYears(years)}
          >
            {allSelected ? "Deselect all years" : "Select all years"}
          </button>
        )}
        {years.map((year) => {
          const withinMin = !minParts || minParts.year === undefined || year >= minParts.year;
          const withinMax = !maxParts || maxParts.year === undefined || year <= maxParts.year;
          const selected = this.isYearSelected(year);
          return (
            <button
              type="button"
              role="option"
              part={selected ? "year year-selected" : "year"}
              data-year={String(year)}
              aria-selected={selected ? "true" : "false"}
              disabled={!interactive || !withinMin || !withinMax}
              onClick={() =>
                this.presentation === "year"
                  ? this.updateSelection(pad(year, 4))
                  : this.selectMonthYearYear(year)
              }
            >
              {year}
            </button>
          );
        })}
      </div>
    );
  }

  private selectMonthYearYear(year: number) {
    if (!this.isInteractive) {
      return;
    }
    this.viewYear = year;
    const month = this.activeParts.month;
    this.updateSelection(`${pad(year, 4)}-${pad(month)}`);
  }

  private renderTime() {
    const interactive = this.isInteractive;
    const hours = parseNumberArray(this.hourValues);
    const minutes = parseNumberArray(this.minuteValues);
    const active = this.activeParts;
    const hasValue = this.workingValues.length > 0;

    return (
      <div part="time" role="group" aria-label="Time">
        <div part="hours" role="listbox" aria-label="Hours">
          {Array.from({ length: 24 }, (_, hour) => hour)
            .filter((hour) => this.isAllowed(hours, hour))
            .map((hour) => (
              <button
                type="button"
                role="option"
                part={hasValue && active.hour === hour ? "hour hour-selected" : "hour"}
                data-hour={String(hour)}
                aria-selected={hasValue && active.hour === hour ? "true" : "false"}
                disabled={!interactive}
                onClick={() => this.updateTimeSelection(hour, null)}
              >
                {pad(hour)}
              </button>
            ))}
        </div>
        <div part="minutes" role="listbox" aria-label="Minutes">
          {Array.from({ length: 60 }, (_, minute) => minute)
            .filter((minute) => this.isAllowed(minutes, minute))
            .map((minute) => (
              <button
                type="button"
                role="option"
                part={hasValue && active.minute === minute ? "minute minute-selected" : "minute"}
                data-minute={String(minute)}
                aria-selected={hasValue && active.minute === minute ? "true" : "false"}
                disabled={!interactive}
                onClick={() => this.updateTimeSelection(null, minute)}
              >
                {pad(minute)}
              </button>
            ))}
        </div>
      </div>
    );
  }

  private renderSurfaces() {
    switch (this.presentation) {
      case "time":
        return this.renderTime();
      case "date":
        return this.renderCalendar();
      case "month":
        return this.renderMonths();
      case "year":
        return this.renderYears();
      case "month-year":
        return [this.renderMonths(), this.renderYears()];
      case "date-time":
      default:
        return [this.renderCalendar(), this.renderTime()];
    }
  }

  // --------------------------------------------
  // Render
  // --------------------------------------------

  render() {
    const serializedValue = this.multiple
      ? JSON.stringify(this.workingValues)
      : this.workingValues[0] ?? "";

    return (
      <Host
        role="group"
        aria-disabled={this.disabled ? "true" : null}
        aria-readonly={this.readonly ? "true" : null}
        data-presentation={this.presentation}
        data-disabled={this.disabled ? "true" : "false"}
        data-readonly={this.readonly ? "true" : "false"}
        data-multiple={this.multiple ? "true" : "false"}
        data-color={this.color ?? null}
        data-value={serializedValue}
        onFocusin={this.handleFocusIn}
        onFocusout={this.handleFocusOut}
      >
        <div part="base">{this.renderSurfaces()}</div>
      </Host>
    );
  }
}
